'use client';

import React from 'react';

import { Heading, Body } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import { PageHeaderBubbleNavigation } from './PageHeaderBubbleNavigation';
import { PageHeaderSellerNavigation } from './PageHeaderSellerNavigation';
import styles from './styles.module.css';

import { PopularBrands } from '@/app/[locale]/(browse)/components/PopularBrands';
import { PageDescription } from '@/components/PageDescription';
import {
  type CMSExplorePage,
  type CMSExplorePageBubbleNavItem,
} from '@/modules/cms/types';
import { useCurrentLanguage } from '@/modules/language/useCurrentLanguage';
import { useProductsSearchWithFallback } from '@/modules/search/useProductsSearchWithFallback';
import { formatNumberWithMetricPrefix } from '@/modules/text/helpers';

export type PageHeaderProps = {
  title: string;
  description?: string;
  testId?: string;
  children?: React.ReactNode;
  bubbleNavItems?: CMSExplorePageBubbleNavItem[];
  featuredShops?: CMSExplorePage['featuredShops'];
  popularBrandSlugs?: string[];
};

export function PageHeader({
  title,
  description,
  children,
  bubbleNavItems = [],
  featuredShops,
  popularBrandSlugs,
}: PageHeaderProps) {
  const t = useTranslations('search');
  const { language } = useCurrentLanguage();
  const { data } = useProductsSearchWithFallback();

  if (bubbleNavItems?.length > 0) {
    return (
      <>
        <PageHeaderBubbleNavigation
          title={title}
          description={description}
          items={bubbleNavItems}
        />
        <PageHeaderSellerNavigation
          title={featuredShops?.showTitle ? featuredShops.title : undefined}
          usernames={featuredShops?.shops || []}
        />
      </>
    );
  }

  const totalCount = data?.pages[0]?.data.meta.total_count;
  const hasTotalCount = typeof totalCount === 'number';
  const hasBorder = Boolean(description);

  return (
    <div
      className={styles.wrapper}
      style={
        {
          '--paddingBottom': hasBorder ? '16px' : 0,
          '--borderBottom': hasBorder ? '1px solid #EEEEEE' : 0,
        } as React.CSSProperties
      }
    >
      <div>
        <Heading variant="lg">{title}</Heading>
        {description && <PageDescription text={description} />}
        <PageHeaderSellerNavigation
          title={featuredShops?.showTitle ? featuredShops.title : undefined}
          usernames={featuredShops?.shops || []}
        />
        <div className={styles.popularBrandsWrapper}>
          <PopularBrands allowedBrandSlugs={popularBrandSlugs} />
        </div>
        {children}
      </div>
      {hasTotalCount && (
        <Body variant="sm-regular" className={styles.resultsTotalCount}>
          {t('LandingPageTotalItems', {
            totalCount: formatNumberWithMetricPrefix(totalCount, language),
          })}
        </Body>
      )}
    </div>
  );
}
